import { CarePlanInterventionResponse } from '~/lib/types/carePlan/carePlans';
import {
  ChakraBox,
  ChakraCollapse,
  FAIconName,
  Icon,
  Text,
} from '@equip.health/ui';
import { useState } from 'react';
import { CarePlanInterventionRow } from '~/components/maud/patients/profile/tabs/careplan/CarePlanInterventionRow';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

type Props = {
  treatmentEpisodeId: string;
  label: string;
  interventions: CarePlanInterventionResponse[];
  phaseOrStageClassification?: 'STAGE' | 'PHASE';
  openByDefault?: boolean;
  isAssignedToMeEnabled?: boolean;
  isAllOtherInterventions?: boolean;
};
export const CarePlanInterventionCollapse = ({
  label,
  interventions,
  phaseOrStageClassification,
  openByDefault = false,
  treatmentEpisodeId,
  isAssignedToMeEnabled = false,
  isAllOtherInterventions = false,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const [isOpen, setIsOpen] = useState(openByDefault);

  const sortedInterventions = interventions.toSorted((i1, i2) => {
    const phaseStage1 = i1.treatmentPhaseStage?.sortOrder ?? Infinity;
    const phaseStage2 = i2.treatmentPhaseStage?.sortOrder ?? Infinity;
    if (phaseStage1 !== phaseStage2) {
      return phaseStage1 - phaseStage2;
    }
    return (
      (i1.intervention?.sortOrder ?? Infinity) -
      (i2.intervention?.sortOrder ?? Infinity)
    );
  });

  let phaseStageLabel = '[phase/stage]';
  if (phaseOrStageClassification === 'STAGE') {
    phaseStageLabel = 'stage';
  } else if (phaseOrStageClassification === 'PHASE') {
    phaseStageLabel = 'phase';
  }

  const emptyInterventionsText = () => {
    if (isAssignedToMeEnabled) {
      return 'No interventions assigned to you.';
    }
    if (!phaseOrStageClassification) {
      return 'No interventions in this section.';
    }
    return `This ${phaseStageLabel} has no associated interventions.`;
  };

  return (
    <ChakraBox marginBottom="12px">
      <ChakraBox
        _hover={{
          bgColor: 'primary.10',
        }}
        as="button"
        borderRadius="7px"
        display="flex"
        gap="6px"
        onClick={() => {
          if (isAllOtherInterventions) {
            if (isOpen) {
              trackEvent(CarePlanEvents.clickOnAllOtherInterventionsToCollapseChevron);
            } else {
              trackEvent(CarePlanEvents.clickOnAllOtherInterventionsToExpandChevron);
            }
          } else {
            trackEvent(
              phaseOrStageClassification
                ? CarePlanEvents.clickOnPhaseStageSectionButtonsInTreatmentPhaseStageComponent
                : CarePlanEvents.clickOnRelatedInterventionsButtonInGoalCards,
              {
                section: label,
              },
            );
          }
          setIsOpen((previous) => !previous);
        }}
        paddingLeft="8px"
        paddingRight="10px"
        paddingY="6px"
      >
        <Icon
          aria-label={`Expand or collapse interventions for ${label}`}
          color="primary.100"
          name={
            isOpen ? FAIconName.chevronUpRegular : FAIconName.chevronDownRegular
          }
        />
        <Text variant="bodyMedium">{label}</Text>
      </ChakraBox>
      <ChakraCollapse in={isOpen}>
        <ChakraBox paddingLeft="42px" paddingY="12px">
          {sortedInterventions.map((intervention) => (
            <CarePlanInterventionRow
              intervention={intervention}
              isForPhaseOrStageClassification={!!phaseOrStageClassification}
              key={intervention.id}
              treatmentEpisodeId={treatmentEpisodeId}
            />
          ))}
          {sortedInterventions.length === 0 && (
            <Text color="neutral.secondary">{emptyInterventionsText()}</Text>
          )}
        </ChakraBox>
      </ChakraCollapse>
    </ChakraBox>
  );
};
